/*
 * "Register device" — two ways to deliver an invite.
 *
 * The link and the QR are two artifacts of one adminInvite call: `inviteUrl` is
 * the https link, the only thing meant to travel, and `qrPngBase64` is the image.
 * One call, both artifacts — never a call per artifact, or the link and the code
 * would be different tokens and only the later one would work.
 *
 * The email path is a *separate* call, and that distinction is load-bearing.
 * SendEnrollmentEmail creates its own token — that is what its 207 reports on —
 * so firing adminInvite eagerly and then sending the email would issue two
 * invites and leave the code already on screen dead. Each path therefore fires
 * only its own call, and only when the admin actually takes that path:
 * adminInvite when "Show invite" is selected, SendEnrollmentEmail on send. Acting
 * on one path supersedes the other, and the dialog says so rather than leaving a
 * stale artifact looking valid.
 *
 * This dialog is mounted by an explicit menu click — never rendered alongside a
 * panel, because the call invalidates whatever invite the user already holds.
 *
 * Copy and Download are deliberately offered here: the admin is expected to
 * paste the link or send the QR image over whatever channel they actually have.
 * (An earlier revision of this flow prohibited both; that was wrong for this
 * workflow and the prohibition has been lifted.) For the same reason there is no
 * close-on-blur — switching apps to paste the link must not shut the dialog and
 * silently invalidate what was just copied.
 *
 * `deepLink` is never rendered as a link. It is QR content and a button target
 * only; a mail client will not linkify an unknown scheme.
 */
import { useEffect, useRef, useState } from "react";
import { Modal, Radio, Spin } from "antd";
import { Check, Copy, Download, Mail, ShieldAlert } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../design-system/tokens";
import { DOMAIN_ID, ORG_ID } from "../../constants";
import { logOutUser } from "../../common";
import { dispatchAdminInvite, dispatchSendConsoleInviteEmail, dispatchSendEnrollmentEmail } from "../../utils/apiservice";
import { countdown, fmtExpiry } from "./enrollmentCommon";

import copyText from "../../utils/copyText";
import OrgAddressHint from "./OrgAddressHint";
const btn = (variant) => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
  padding: "7px 12px", fontFamily: AN_FONT, fontSize: 12.5, fontWeight: 500,
  borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap",
  color: variant === "primary" ? "#fff" : AN_COLORS.ink2,
  background: variant === "primary" ? AN_COLORS.primary : "#fff",
  border: variant === "primary" ? "none" : `1px solid ${AN_COLORS.line}`,
});

const initialMode = (email) => (email && String(email).trim() ? "email" : "show");

// userType: "directory" (an AD user, the default) or "console" (a console
// user from Settings → Users & Access). They are emailed through different
// endpoints; see sendEmail.
export default function RegisterDeviceDialog({ open, onClose, email, userId, onNotify, userType = "directory" }) {
  const hasEmail = !!email && !!String(email).trim();

  // Resolved in the initialiser, not in an effect. Defaulting to "show" and then
  // correcting it afterwards made the invite effect fire on mount against a mode
  // that was about to change — the resulting request was orphaned by that
  // effect's own cleanup and `loading` never cleared, so the dialog span forever
  // on a request that had already succeeded.
  const [mode, setMode] = useState(() => initialMode(email));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendState, setSendState] = useState(null); // "sent" | "partial"
  // One invite per "Show invite" session. A ref, not `loading`/`data`, so that
  // no state flip can re-enter the effect or wedge it shut.
  const requestedRef = useRef(false);

  // Reset per opening. Without an address on the record there is nothing to mail,
  // so start on the path that can actually work.
  useEffect(() => {
    if (!open) return;
    setError("");
    setData(null);
    setSendState(null);
    setCopied(false);
    setMode(initialMode(email));
    requestedRef.current = false;
  }, [open, email]);

  // Issue the invite only when the admin is actually on the "Show invite" path.
  // Deps are just (open, mode): `data` must not be one, or storing the result
  // would re-run this effect and its cleanup would cancel the very request that
  // produced it.
  useEffect(() => {
    if (!open || mode !== "show" || requestedRef.current) return;
    requestedRef.current = true;
    setLoading(true);
    setError("");

    // Every handler clears `loading` unconditionally. A stale response cannot
    // arrive here: dispatchEnrollment aborts any in-flight adminInvite when a new
    // one starts, and aborted requests fire no callback at all.
    const failed = (msg) => {
      setLoading(false);
      setError(msg);
      // Let switching back to this path retry rather than dead-ending.
      requestedRef.current = false;
    };

    dispatchAdminInvite({
      email,
      onSuccess: (res) => {
        setLoading(false);
        setData(res);
        setRemaining(countdown(res?.expiresAt));
      },
      // The menu item is hidden for non-admins, so a 403 means the role gate and
      // the server disagree — a bug to log, not a message to render.
      onForbidden: ({ message: m }) => {
        console.error(
          `[enrollment] adminInvite returned 403 (${m}) for a user the UI treated as an admin. ` +
          `The role gate and the server disagree.`
        );
        failed("Something went wrong creating this invite. Please report this.");
      },
      onNotFound: () => failed("This user isn't in this organisation."),
      onFailure: ({ message: m, isLogout }) => {
        if (isLogout) { setLoading(false); logOutUser(); return; }
        failed(m || "Could not create an enrollment invite.");
      },
      onError: ({ message: m }) => failed(m || "Could not create an enrollment invite."),
    });
  }, [open, mode, email]);

  useEffect(() => {
    if (!open || !data?.expiresAt) return;
    const id = setInterval(() => setRemaining(countdown(data.expiresAt)), 60000);
    return () => clearInterval(id);
  }, [open, data?.expiresAt]);

  const close = () => {
    setData(null);
    setError("");
    setRemaining(null);
    setCopied(false);
    setSendState(null);
    onClose?.();
  };

  const inviteUrl = data?.inviteUrl || "";

  const copyLink = async () => {
    if (!inviteUrl) return;
    try {
      await copyText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onNotify?.({ type: "error", content: "Could not copy the link." });
    }
  };

  const downloadQr = () => {
    if (!data?.qrPngBase64) return;
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${data.qrPngBase64}`;
    a.download = `authnull-enrollment-${(data.email || email || "user").replace(/[^\w.@-]/g, "_")}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Sends the invite that already exists rather than minting a second one.
  const sendEmail = () => {
    setSending(true);
    // A console user's id is not an AD user id, so it must never reach
    // SendEnrollmentEmail: that 404s, or emails whichever AD user shares the id.
    const send = userType === "console" ? dispatchSendConsoleInviteEmail : dispatchSendEnrollmentEmail;
    send({
      payload:
        userType === "console"
          ? { email, tenantId: DOMAIN_ID(), orgId: ORG_ID() }
          : { orgId: ORG_ID(), tenantId: DOMAIN_ID(), userId, email },
      onSuccess: () => {
        setSending(false);
        setSendState("sent");
        // The emailed invite supersedes any code shown earlier in this dialog, so
        // drop it rather than leaving a dead QR one radio click away. Releasing
        // the ref lets "Show invite" issue a fresh one.
        setData(null);
        requestedRef.current = false;
        onNotify?.({ type: "success", content: `Enrollment invite sent to ${email}.` });
      },
      // 207: the token exists, SMTP failed. Never report this as sent.
      onPartial: () => {
        setSending(false);
        setSendState("partial");
        setData(null);
        requestedRef.current = false;
        onNotify?.({
          type: "warning",
          content: "Invite created, but the email could not be sent. Share the link or QR instead.",
        });
      },
      onNotFound: () => {
        setSending(false);
        onNotify?.({ type: "error", content: "This user isn't in this organisation." });
      },
      onFailure: ({ message: m, isLogout }) => {
        setSending(false);
        if (isLogout) { logOutUser(); return; }
        onNotify?.({ type: "error", content: m || "Could not send the invite." });
      },
      onError: ({ message: m }) => {
        setSending(false);
        onNotify?.({ type: "error", content: m || "Could not send the invite." });
      },
    });
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      destroyOnClose
      width={470}
      title={
        <span style={{ fontFamily: AN_FONT, fontSize: 15, fontWeight: 600, color: AN_COLORS.ink }}>
          Register device — <span style={{ fontFamily: AN_MONO, fontSize: 13 }}>{email || "user"}</span>
        </span>
      }
    >
      <div style={{ fontFamily: AN_FONT }}>
        {/* Always available, both paths, whatever the record looks like. */}
        <Radio.Group
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}
        >
          <Radio value="email" disabled={!hasEmail}>
            <span style={{ fontSize: 13, color: AN_COLORS.ink }}>Send by email</span>
            <span style={{ fontFamily: AN_MONO, fontSize: 11.5, color: AN_COLORS.muted, marginLeft: 8 }}>
              {hasEmail ? email : "no email address on this record"}
            </span>
          </Radio>
          <Radio value="show">
            <span style={{ fontSize: 13, color: AN_COLORS.ink }}>Show invite</span>
            <span style={{ fontSize: 11.5, color: AN_COLORS.muted, marginLeft: 8 }}>
              copy the link or the QR and send it yourself
            </span>
          </Radio>
        </Radio.Group>

        {mode === "email" && (
          <div style={{ paddingLeft: 24 }}>
            <button type="button" onClick={sendEmail} disabled={sending || sendState === "sent"}
              style={{ ...btn("primary"), opacity: sending || sendState === "sent" ? 0.6 : 1 }}>
              <Mail size={13} />
              {sending ? "Sending…" : sendState === "sent" ? "Sent" : "Send invite"}
            </button>

            {/* Switching to the other path issues a fresh invite and kills this
                one, so say it here rather than letting the admin discover it. */}
            {sendState === "sent" && (
              <div style={{ marginTop: 10, fontSize: 12, color: AN_COLORS.muted, lineHeight: 1.5 }}>
                Sent to {email}. Showing the invite instead will issue a new one and invalidate
                the emailed link.
              </div>
            )}

            {sendState === "partial" && (
              <div style={{
                marginTop: 10, padding: "9px 12px", background: "#FFF8EC",
                border: "1px solid #F3E2C0", borderRadius: 8, fontSize: 12.5,
                color: AN_COLORS.ink2, lineHeight: 1.5,
              }}>
                Invite created, but the email could not be sent. Switch to{" "}
                <strong style={{ fontWeight: 600 }}>Show invite</strong> to issue one you can share
                yourself.
              </div>
            )}
          </div>
        )}

        {mode === "show" && loading && (
          <div style={{ padding: "40px 0", textAlign: "center" }}><Spin /></div>
        )}

        {mode === "show" && !loading && error && (
          <div style={{
            padding: "12px 14px", background: "var(--an-dangerBg)", border: "1px solid var(--an-dangerBd)",
            borderRadius: 8, fontSize: 13, color: AN_COLORS.danger,
          }}>
            {error}
          </div>
        )}

        {mode === "show" && !loading && !error && data && (
          <>
            <div style={{ paddingLeft: 24, display: "flex", gap: 16, alignItems: "flex-start" }}>
                {/* Server-rendered PNG. Not re-encoded from `payload`: the server
                    tunes the QR version to stay scannable off a laptop screen. */}
                {data.qrPngBase64 ? (
                  <img
                    src={`data:image/png;base64,${data.qrPngBase64}`}
                    alt={`Enrollment QR code for ${data.email || email}`}
                    style={{
                      width: 130, height: 130, flexShrink: 0, imageRendering: "pixelated",
                      border: `1px solid ${AN_COLORS.line}`, borderRadius: 8, padding: 4,
                      background: "#FFFFFF",
                    }}
                  />
                ) : (
                  <div style={{
                    width: 130, height: 130, flexShrink: 0, display: "flex", alignItems: "center",
                    justifyContent: "center", border: `1px dashed ${AN_COLORS.line}`,
                    borderRadius: 8, fontSize: 11.5, color: AN_COLORS.muted, textAlign: "center",
                    padding: 8,
                  }}>
                    No QR image returned
                  </div>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: AN_MONO, fontSize: 10, letterSpacing: 1.1,
                    textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 6,
                  }}>
                    Invite link
                  </div>

                  {inviteUrl ? (
                    <>
                      <div style={{
                        fontFamily: AN_MONO, fontSize: 11.5, color: AN_COLORS.ink2,
                        wordBreak: "break-all", lineHeight: 1.45, marginBottom: 10,
                        maxHeight: 54, overflow: "hidden",
                      }}>
                        {inviteUrl}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" onClick={copyLink} style={btn()}>
                          {copied ? <Check size={13} color={AN_COLORS.success} /> : <Copy size={13} />}
                          {copied ? "Copied" : "Copy"}
                        </button>
                        {data.qrPngBase64 && (
                          <button type="button" onClick={downloadQr} style={btn()}>
                            <Download size={13} /> Download QR
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    // No console URL is configured server-side, so there is no
                    // https link to hand out. The QR still works.
                    <>
                      <div style={{ fontSize: 12.5, color: AN_COLORS.ink2, lineHeight: 1.5, marginBottom: 10 }}>
                        No invite link is available — this deployment has no console URL configured.
                        Have the user scan the QR code instead.
                      </div>
                      {data.qrPngBase64 && (
                        <button type="button" onClick={downloadQr} style={btn()}>
                          <Download size={13} /> Download QR
                        </button>
                      )}
                    </>
                  )}
                </div>
            </div>
            <OrgAddressHint activationCode={data.activationCode} consoleHost={data.consoleHost} />

            {/* What the invite grants, stated once for both paths. */}
            <div style={{
              display: "flex", gap: 9, alignItems: "flex-start", marginTop: 18,
              padding: "11px 13px", background: "#FFF8EC", border: "1px solid #F3E2C0",
              borderRadius: 8, fontSize: 12.5, lineHeight: 1.5, color: AN_COLORS.ink2,
            }}>
              <ShieldAlert size={15} color={AN_COLORS.warn} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Anyone who opens this can register a device as{" "}
                <strong style={{ fontWeight: 600 }}>{data.email || email}</strong>.
                {" "}Expires {fmtExpiry(data.expiresAt)}
                {remaining && remaining !== "expired" ? ` (in ${remaining})` : ""} · works once ·
                re-issuing replaces it.
              </span>
            </div>

            <div style={{ marginTop: 12, fontSize: 11.5, lineHeight: 1.5, color: AN_COLORS.muted }}>
              {(data.orgName || data.orgHost) && (
                <>
                  {data.orgName}
                  {data.orgHost && <span style={{ fontFamily: AN_MONO }}> · {data.orgHost}</span>}
                  {" — "}
                </>
              )}
              creating this invite is recorded against your account.
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
